import { useEffect, useRef } from 'react';
import { Link } from 'wouter';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { TypingIndicator } from './TypingIndicator';
import { useLang } from '@/lib/i18n';
import type { useChat } from '@/hooks/useChat';

type ChatData = ReturnType<typeof useChat>;

interface ChatWindowProps extends Pick<ChatData, 'messages' | 'typingUsers' | 'isClosed' | 'connectionStatus' | 'watchdogState' | 'sendMessage' | 'sendPrivateMessage' | 'sendTyping' | 'sendStopTyping'> {
  name: string;
  dmTarget?: string;
  onClearDm: () => void;
}

export function ChatWindow({
  name,
  dmTarget,
  onClearDm,
  messages,
  typingUsers,
  isClosed,
  connectionStatus,
  watchdogState,
  sendMessage,
  sendPrivateMessage,
  sendTyping,
  sendStopTyping,
}: ChatWindowProps) {
  const { t } = useLang();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesContentRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);

  const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.scrollTop = container.scrollHeight;
  };

  // Detect whether the user is intentionally reading older messages.
  // When close to the bottom, new messages/media keep the view pinned.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;

      shouldAutoScrollRef.current = distanceFromBottom <= 160;
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  // New messages: use the actual rendered DOM height, not only React timing.
  useEffect(() => {
    if (!shouldAutoScrollRef.current) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollToBottom('auto'));
    });
  }, [messages]);

  // Images, videos, audio previews and attachments can change their height
  // after React has rendered the message. Observe the message content itself.
  useEffect(() => {
    const container = messagesContainerRef.current;
    const content = messagesContentRef.current;
    if (!container || !content) return;

    const keepBottom = () => {
      if (shouldAutoScrollRef.current) {
        requestAnimationFrame(() => scrollToBottom('auto'));
      }
    };

    const resizeObserver = new ResizeObserver(keepBottom);
    resizeObserver.observe(content);

    const mutationObserver = new MutationObserver(keepBottom);
    mutationObserver.observe(content, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // Capture media loading after the message is already displayed.
    const onMediaLoad = () => keepBottom();
    content.addEventListener('load', onMediaLoad, true);
    content.addEventListener('loadedmetadata', onMediaLoad, true);
    content.addEventListener('canplay', onMediaLoad, true);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      content.removeEventListener('load', onMediaLoad, true);
      content.removeEventListener('loadedmetadata', onMediaLoad, true);
      content.removeEventListener('canplay', onMediaLoad, true);
    };
  }, [messages.length]);

  // Always make the initial conversation view start at the latest message.
  useEffect(() => {
    requestAnimationFrame(() => scrollToBottom('auto'));
  }, []);
  if (isClosed) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center animate-in fade-in duration-700">
        <div className="bg-card/50 backdrop-blur-xl border border-border/50 p-8 rounded-2xl max-w-md w-full shadow-xl">
          <div className="w-16 h-16 mx-auto mb-6 bg-destructive/10 rounded-full flex items-center justify-center text-destructive">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </div>
          <h2 className="text-2xl font-medium mb-3">{t('chat.roomClosed')}</h2>
          <p className="text-muted-foreground mb-8">{t('chat.roomClosedDesc')}</p>
          
          <Link href="/" className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6 py-2">
            {t('chat.goHome')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full bg-background relative overflow-hidden">
      {(connectionStatus === 'reconnecting' || watchdogState !== 'CONNECTED') && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 bg-destructive/90 text-destructive-foreground text-xs px-4 py-1.5 rounded-full shadow-lg backdrop-blur-md animate-in slide-in-from-top-2">
          {connectionStatus === 'reconnecting' ? t('chat.reconnecting') : `watchdog: ${watchdogState.toLowerCase()}`}
        </div>
      )}

      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 sm:px-4 md:px-8 [-webkit-overflow-scrolling:touch]"
      >
        <div ref={messagesContentRef} className="space-y-2">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center opacity-50 select-none">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="mb-4 text-muted-foreground"><path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2v5Z"/><path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1"/></svg>
            <p className="text-lg text-muted-foreground font-medium">{t('chat.quiet')}</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-xs">{t('chat.noMessages')}</p>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <ChatMessage
                key={msg.id}
                message={msg}
                isMe={msg.type === 'message' && msg.from === name}
                myName={name}
              />
            ))}
            <div className="h-2"></div>
          </>
        )}
        
        <TypingIndicator users={typingUsers} />
        </div>
      </div>

      <ChatInput
        onSendMessage={sendMessage}
        onSendPrivateMessage={sendPrivateMessage}
        onTyping={sendTyping}
        onStopTyping={sendStopTyping}
        disabled={connectionStatus !== 'connected'}
        dmTarget={dmTarget}
        onClearDm={onClearDm}
      />
    </div>
  );
}
